import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PropertyCard from "@/components/locanest/PropertyCard";
import LocationSearch from "@/components/locanest/LocationSearch";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORY_LIST, CATEGORY_MAP, CATEGORY_FILTERS } from "@/lib/categories";

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [favs, setFavs] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);

  const category = params.get("category") || "all";
  const city = params.get("city") || "all";
  const locality = params.get("locality") || "";
  const near = params.get("near") || "";
  const min_rent = params.get("min_rent") || "";
  const max_rent = params.get("max_rent") || "";
  const q = params.get("q") || "";
  const [error, setError] = useState(false);
  const [nearMeta, setNearMeta] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [city !== "all", !!min_rent, !!max_rent, ...[...params.keys()].map((k) => k.startsWith("cf_"))].filter(Boolean).length;
  const catFilters = category !== "all" ? (CATEGORY_FILTERS[category] || []) : [];

  const update = (k, v) => {
    const np = new URLSearchParams(params);
    if (v && v !== "all") np.set(k, v); else np.delete(k);
    // Clearing category-specific filters when category changes (prevent orphan cf_* params)
    if (k === "category") {
      for (const key of Array.from(np.keys())) {
        if (key.startsWith("cf_")) np.delete(key);
      }
    }
    setParams(np);
  };

  const clearAll = () => setParams(new URLSearchParams());

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setNearMeta(null);
    const nearParts = near.split(",").map(Number);
    if (near && nearParts.length === 2 && nearParts.every((n) => Number.isFinite(n))) {
      const np = { lat: nearParts[0], lng: nearParts[1], limit: 60 };
      if (category && category !== "all") np.category = category;
      api.get("/properties/near", { params: np })
        .then(({ data }) => { setProps(data.items); setNearMeta(data); })
        .catch(() => { setProps([]); setError(true); })
        .finally(() => setLoading(false));
      return;
    }
    const queryParams = {};
    if (category && category !== "all") queryParams.category = category;
    if (city && city !== "all") queryParams.city = city;
    if (locality) queryParams.locality = locality;
    if (min_rent) queryParams.min_rent = min_rent;
    if (max_rent) queryParams.max_rent = max_rent;
    if (q) queryParams.q = q;
    // Pass category-specific filters as cf_<key>
    for (const [k, v] of params.entries()) {
      if (k.startsWith("cf_") && v) queryParams[k] = v;
    }
    api.get("/properties", { params: queryParams })
      .then(({ data }) => setProps(data))
      .catch(() => { setProps([]); setError(true); })
      .finally(() => setLoading(false));
  }, [params, category, city, locality, near, min_rent, max_rent, q, reloadKey]);

  const hasFilters = Array.from(params.keys()).length > 0;

  useEffect(() => {
    if (user?.role === "tenant") {
      api.get("/favorites").then(({ data }) => setFavs(new Set(data.map((p) => p.id)))).catch(() => {});
    }
  }, [user]);

  const toggleFav = async (id) => {
    if (!user) { window.location.href = "/login"; return; }
    const wasFav = favs.has(id);
    if (wasFav) {
      await api.delete(`/favorites/${id}`);
      setFavs((prev) => { const next = new Set(prev); next.delete(id); return next; });
    } else {
      await api.post(`/favorites/${id}`);
      setFavs((prev) => { const next = new Set(prev); next.add(id); return next; });
    }
  };

  const isShortStay = category === "short_stay";
  const catLabel = category === "all" ? "All rentals" : (CATEGORY_MAP[category]?.label || category);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Browse</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2" data-testid="listings-heading">{catLabel}</h1>
          <div className="text-sm text-[#5C6B7A] mt-1" data-testid="listings-scope">
            {near ? (nearMeta ? `Near your location${nearMeta.nearest_city ? ` · ${nearMeta.nearest_city}` : ""}${nearMeta.expanded_to_city ? " (nothing within 15 km — showing the nearest city)" : ` · within ${nearMeta.radius_km} km`}` : "Near your location") : locality ? `${locality}${city !== "all" ? `, ${city}` : ""}` : city === "all" ? "Across India" : city}
          </div>
        </div>
        <div className="text-sm text-[#5C6B7A]" data-testid="results-count">{props.length} properties</div>
      </div>

      {/* CATEGORY CHIPS */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1" data-testid="category-chips">
        <button
          onClick={() => update("category", "all")}
          className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition ${category === "all" ? "bg-[#1A1F2C] text-white border-[#1A1F2C]" : "bg-[#FDFBF7] border-[#1A1F2C]/15 hover:border-[#C85A32]"}`}
        >All</button>
        {CATEGORY_LIST.map((c) => (
          <button
            key={c.key}
            onClick={() => update("category", c.key)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition ${category === c.key ? "bg-[#C85A32] text-white border-[#C85A32]" : "bg-[#FDFBF7] border-[#1A1F2C]/15 hover:border-[#C85A32]"}`}
            data-testid={`chip-${c.key}`}
          >{c.label}</button>
        ))}
      </div>

      {/* CORE FILTERS */}
      <div className="bg-[#F0EBE1] border border-[#1A1F2C]/10 rounded-lg p-4 md:p-5 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="filters-bar">
        <div className="col-span-2 md:col-span-2 flex items-start gap-2">
          <div className="flex-1 min-w-0">
          <LocationSearch
            compact
            value={locality || (near ? "📍 Near me" : "") || q}
            onChange={(t) => { if (!t) { const np = new URLSearchParams(params); np.delete("locality"); np.delete("near"); np.delete("q"); setParams(np); } }}
            onPick={(it) => { const np = new URLSearchParams(params); np.delete("near"); np.delete("q"); if (it.city) np.set("city", it.city); else np.delete("city"); if (it.locality) np.set("locality", it.locality); else np.delete("locality"); setParams(np); }}
            onNear={({ lat, lng }) => { const np = new URLSearchParams(params); np.delete("city"); np.delete("locality"); np.delete("q"); np.set("near", `${lat},${lng}`); setParams(np); }}
            placeholder="Search area, locality, city…"
            testId="filter-location"
          />
          </div>
          <Button type="button" variant="outline" onClick={() => setShowFilters((v) => !v)} className="md:hidden h-10 shrink-0 rounded-md relative" aria-expanded={showFilters} data-testid="toggle-filters">
            <SlidersHorizontal size={15} />
            {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C85A32] text-white text-[10px] grid place-items-center" data-testid="active-filter-count">{activeFilterCount}</span>}
          </Button>
        </div>
        <div className={`${showFilters ? "contents" : "hidden"} md:contents`} data-testid="filters-advanced">
        <Select value={city} onValueChange={(v) => update("city", v)}>
          <SelectTrigger data-testid="filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 col-span-2 md:col-span-2">
          {!isShortStay && <Input type="number" placeholder="Min ₹" value={min_rent} onChange={(e) => update("min_rent", e.target.value)} className="h-10" data-testid="filter-min-rent" />}
          {!isShortStay && <Input type="number" placeholder="Max ₹" value={max_rent} onChange={(e) => update("max_rent", e.target.value)} className="h-10" data-testid="filter-max-rent" />}
          {isShortStay && <div className="text-xs text-[#5C6B7A] col-span-2">Use "Max ₹/night" filter below</div>}
        </div>
        <Button variant="outline" onClick={clearAll} className="rounded-md" data-testid="clear-filters">
          <X size={14} className="mr-1.5" /> Clear
        </Button>

        {/* CATEGORY-SPECIFIC FILTERS */}
        {catFilters.map((f) => {
          const paramKey = `cf_${f.min ? `min_${f.target}` : f.max ? `max_${f.target}` : f.key}`;
          const val = params.get(paramKey) || "";
          if (f.type === "select") {
            return (
              <Select key={f.key} value={val || "all"} onValueChange={(v) => update(paramKey, v)}>
                <SelectTrigger data-testid={`cf-filter-${f.key}`}><SelectValue placeholder={f.label} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{f.label}: Any</SelectItem>
                  {f.options.map((o) => {
                    const [k, l] = Array.isArray(o) ? o : [o, o];
                    return <SelectItem key={k} value={k}>{l}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            );
          }
          return (
            <Input
              key={f.key}
              type={f.type}
              placeholder={f.label}
              value={val}
              onChange={(e) => update(paramKey, e.target.value)}
              className="h-10"
              data-testid={`cf-filter-${f.key}`}
            />
          );
        })}
        </div>
      </div>

      {/* RESULTS */}
      <div className="mt-8">
        {loading ? (
          <div className="text-[#5C6B7A]">Loading…</div>
        ) : error ? (
          <div className="text-center py-20 text-[#5C6B7A]" data-testid="listings-error">
            <SlidersHorizontal size={40} className="mx-auto mb-3 opacity-50" />
            Couldn&rsquo;t load listings right now. <button onClick={() => setReloadKey((k) => k + 1)} className="text-[#C85A32] font-medium underline" data-testid="listings-retry">Retry</button>
          </div>
        ) : props.length === 0 ? (
          <div className="text-center py-20 text-[#5C6B7A]" data-testid="listings-empty">
            <SlidersHorizontal size={40} className="mx-auto mb-3 opacity-50" />
            {hasFilters ? <>No properties match your filters. <button onClick={clearAll} className="text-[#C85A32] font-medium underline" data-testid="listings-empty-clear">Show all listings</button></> : "No listings published yet — check back soon."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="results-grid">
            {props.map((p) => (
              <div key={p.id} className="relative">
                <PropertyCard p={p} isFav={favs.has(p.id)} onFavToggle={user?.role === "tenant" ? toggleFav : undefined} />
                {near && p.distance_km != null && <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#1A1F2C]/75 text-white text-[10px] tabular-nums" data-testid={`distance-${p.id}`}>{p.distance_km < 1 ? "< 1 km" : `${p.distance_km} km`}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
